import { deleteCookie, getCookieValue, trackEvent } from '@do/seashell';
import { useQuery } from '@do/seashell/graphql';
import { useLocation, useNavigate } from '@do/seashell/navigation';
import { Box, style } from '@do/walrus';
import _get from 'lodash/get';
import React, { ReactElement, useCallback, useEffect, useState } from 'react';
import { createClient } from '../client';
import {
  CHURN_SURVEY_URL,
  FLASH_ERROR_COOKIE_NAME,
  GITHUB_STUDENT_PACK,
  ONBOARDING_ORIGIN_PARAM,
  REGISTER_ENDPOINT,
  REGISTER_SHOW_OPTIONS_STEP
} from '../constants';
import copy from '../copy';
import { GetRegistrationInfo } from '../generated-types/GetRegistrationInfo';
import {
  registerReferralCode,
  registerReferralCodeVariables
} from '../generated-types/registerReferralCode';
import useActivationRedirectCookie from '../hooks/use-activation-redirect-cookie';
import useDocumentTitle from '../hooks/use-document-title';
import useGithubStudentCookie from '../hooks/use-github-student-cookie';
import useOneTimeCookie from '../hooks/use-one-time-cookie';
import { VariantProps } from '../interfaces/variant';
import {
  GET_REGISTRATION_FORM_DATA,
  REGISTER_REFERRAL_CODE
} from '../queries/queries';
import registrationVariants from '../registration-variants';
import { getRegisterRefcode, setRegisterCookie } from '../utils/get-refcode';
import { getSessionData } from '../utils/gql-utils';
import { getQueryParam } from '../utils/query-utils';
import { getRegisterVariant } from '../utils/variant-utils';
import Button from './Button';
import { BannerType, HeaderBanner, Loading, Wrap, WrapSplit } from './Common';
import CreditBanner from './CreditBanner';
import CreditText from './CreditText';
import DOLink from './DOLink';
import ErrorBanner from './ErrorBanner';
import FooterWrap from './FooterWrap';
import FormWrap from './FormWrap';
import GithubButton from './GithubButton';
import GoogleButton from './GoogleButton';
import Header from './Header';
import Link from './Link';
import Metadata from './Metadata';
import RegistrationForm from './RegistrationForm';
import { StepTransitionManager } from './StepTransitionManager';
import Text from './Text';
import Title from './Title';
import { Variant } from './Variant';
import Wrapper from './Wrapper';

const client = createClient(REGISTER_ENDPOINT);
const userUUID = getSessionData('ajs_user_id').replace(/(^"|"$)/g, '');
const churnSurvey = userUUID
  ? `${CHURN_SURVEY_URL}?uuid=${userUUID}`
  : CHURN_SURVEY_URL;

const WrapContent: React.FC<{
  children: React.ReactNode;
  data: GetRegistrationInfo | undefined;
  step: number;
  variant?: VariantProps;
}> = ({ children, data, step, variant }) => {
  const WrapComponent = variant?.splash ? WrapSplit : Wrap;

  const isCreditBanner = !!_get(data, 'registrationFormData.is_credit');

  return (
    <>
      {isCreditBanner && (
        <CreditBanner>
          <strong>{copy.credit.title}</strong>
          <CreditText>{copy.credit.details}</CreditText>
        </CreditBanner>
      )}
      <WrapComponent>
        <FormWrap
          className={
            isCreditBanner
              ? variant?.splash
                ? 'isCreditBannerSplit'
                : 'isCreditBanner'
              : ''
          }
        >
          <Wrapper>
            <StepTransitionManager currentStepNum={step}>
              <Box display="block" px={style.vars.whitespace.medium} pb={2}>
                {children}
              </Box>
            </StepTransitionManager>
          </Wrapper>
        </FormWrap>
        <FooterWrap style={{ color: variant?.footerTextColor }}>
          {copy.footer.terms_before_link + ' '}
          <DOLink path="/legal/terms/" color={variant?.footerLinkColor}>
            {copy.footer.terms_link_text}
          </DOLink>
          {' ' + copy.footer.privacy_before_link + ' '}
          <DOLink path="/legal/privacy/" color={variant?.footerLinkColor}>
            {copy.footer.privacy_link_text}
          </DOLink>
        </FooterWrap>
      </WrapComponent>
    </>
  );
};

type FlashError = {
  code: string;
  field: string;
};

function Registration({
  step = REGISTER_SHOW_OPTIONS_STEP
}: {
  step?: number;
}): ReactElement {
  const [flashErrorCode, setFlashErrorCode] = useState('');
  const flashError = getCookieValue(FLASH_ERROR_COOKIE_NAME);
  useEffect(() => {
    if (flashError) {
      setFlashErrorCode((flashError as unknown as FlashError[])[0]?.code);
      deleteCookie(FLASH_ERROR_COOKIE_NAME);
    }
  }, [flashError]);

  useActivationRedirectCookie();

  const location = useLocation();
  const navigate = useNavigate();
  const refcode = getRegisterRefcode(location.search);
  useQuery<registerReferralCode, registerReferralCodeVariables>(
    REGISTER_REFERRAL_CODE,
    {
      variables: { refcode },
      skip: !refcode
    }
  );
  setRegisterCookie(refcode);

  const isUserAccountDeactivated = useOneTimeCookie(
    '_digitalocean_flash_code',
    'user_account_deactivated'
  );

  const onboardingOrigin =
    getQueryParam(ONBOARDING_ORIGIN_PARAM, location.search) || '';
  const isGitHubStudentVariant = onboardingOrigin === GITHUB_STUDENT_PACK;
  useGithubStudentCookie(isGitHubStudentVariant);

  const { loading: isLoadingFormData, data } = useQuery<GetRegistrationInfo>(
    GET_REGISTRATION_FORM_DATA,
    {
      client,
      variables: {
        onboarding_origin: onboardingOrigin,
        redirect_url: getQueryParam('redirect_url', location.search)
      }
    }
  );
  const sessionId = data?.registrationFormData?.session_id;

  let variant: VariantProps | false;

  if (isGitHubStudentVariant) {
    variant = registrationVariants.githubStudentPack;
  } else if (!refcode) {
    variant = !isLoadingFormData && getRegisterVariant('', data);
  } else {
    variant = !isLoadingFormData && getRegisterVariant(refcode, data);
  }

  useDocumentTitle(_get(variant, 'title'));

  useEffect(() => {
    if (isLoadingFormData) return;

    trackEvent('Registration Variant', {
      variantType: _get(variant, 'type')
    });
  }, [isLoadingFormData]);

  const oauthLinkClickHandler = useCallback((url: string) => {
    return async (e: MouseEvent) => {
      e.preventDefault();
      window.location.assign(url);
    };
  }, []);

  if (!variant) {
    return <Loading />;
  }

  const oauthError = _get(data, 'registrationFormData.oauth_error');
  const googleOauthUrl = `/registrations/google${location.search}`;
  const githubOauthUrl = `/registrations/github${location.search}`;
  const signInUrl = _get(
    data,
    'registrationFormData.marketplace_app.create_app_url'
  );
  const registrationEmail = _get(data, 'registrationFormData.email');

  const OauthSection = (): ReactElement => (
    <>
      <Text>
        {copy.form.oauth.title + ' '}
        <Link forceExternal={true} target="_self" to={googleOauthUrl}>
          {copy.form.oauth.google}
        </Link>
        {' ' + copy.form.or + ' '}
        <Link forceExternal={true} target="_self" to={githubOauthUrl}>
          {copy.form.oauth.github}
        </Link>
        {' ' + copy.form.oauth.instead}
      </Text>
    </>
  );

  let form;
  const errorCode = oauthError || flashErrorCode;
  if (step === REGISTER_SHOW_OPTIONS_STEP) {
    const pretext = (
      <>
        {errorCode && (
          <ErrorBanner error={copy.errors[errorCode] || copy.errors.unknown} />
        )}
        <Title title={variant.title} />
        {variant.metadata ? (
          <Metadata variant={variant} data={data} />
        ) : (
          <Text>{variant.subtitle}</Text>
        )}
      </>
    );

    if (variant.type === 'team' && registrationEmail) {
      form = (
        <>
          {pretext}
          <OauthSection />
          <RegistrationForm
            sessionId={sessionId as string}
            email={registrationEmail}
            onboardingOrigin={onboardingOrigin}
          />
        </>
      );
    } else {
      const emailButton = (
        <Button
          fullWidth={true}
          onClick={() => {
            navigate(`/registrations/email${location?.search}`);
          }}
        >
          {copy.form.email_signup_title}
        </Button>
      );

      if (isGitHubStudentVariant) {
        form = (
          <>
            {pretext}
            <GithubButton
              text={copy.form.oauth.register.github_button}
              onClick={oauthLinkClickHandler(githubOauthUrl)}
            />
          </>
        );
      } else {
        form = (
          <>
            {pretext}
            <GoogleButton
              text={copy.form.oauth.register.google_button}
              onClick={oauthLinkClickHandler(googleOauthUrl)}
            />
            <GithubButton
              text={copy.form.oauth.register.github_button}
              onClick={oauthLinkClickHandler(githubOauthUrl)}
            />
            {emailButton}
          </>
        );
      }
    }
  } else {
    form = (
      <>
        <Title title={copy.form.email_signup_title} />
        <OauthSection />
        <RegistrationForm
          sessionId={sessionId as string}
          email={registrationEmail}
          onboardingOrigin={onboardingOrigin}
        />
      </>
    );
  }

  return (
    <>
      <Header
        forceExternal={!!signInUrl}
        url={signInUrl}
        queryString={location.search}
      />
      {isUserAccountDeactivated && (
        <HeaderBanner
          type={BannerType.Success}
          text={copy.deactivation_message}
          linkText={copy.survey_text}
          url={churnSurvey}
        />
      )}
      <Variant variant={variant}>
        <WrapContent data={data} step={step} variant={variant}>
          {form}
        </WrapContent>
      </Variant>
    </>
  );
}

export default Registration;
